import {
  Button,
  cn,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  generateTimeOptions,
  Label,
  TimePicker,
} from "@win-reality/react-ui";
import { useState } from "react";

import { useTimeSlotAvailability } from "@/components/EventForm/TimeSlotAvailability/TimeSlotAvailability.queries";
import {
  getEventLabel,
  getEventTimeRange,
} from "@/components/EventForm/TimeSlotAvailability/TimeSlotAvailability.utils";

import {
  atTimeOfDay,
  describeEndTimeChange,
  formatTimeOfDay,
  getEndTimeOptions,
  toTimeOfDay,
} from "../../utils/gameEndTime";
import { ThemedDialog } from "../ThemedDialog";

const TIME_OPTIONS = generateTimeOptions("15m");

type GameEndTimeDialogProps = {
  /** The countdown, e.g. "89:04" */
  remaining: string;
  /** The current scheduled end, epoch ms */
  endTimeMs: number;
  now: number;
  gameId: string;
  machineUuid: string;
  onClose: () => void;
  /** Saves the new end, epoch seconds */
  onSave: (endTimeS: number) => Promise<void>;
  onEndGame: () => void;
};

/**
 * Moves the game's end. A later end is checked against the field's schedule
 * the way scheduling an event is, and can't be saved over another event.
 */
export const GameEndTimeDialog = ({
  remaining,
  endTimeMs,
  now,
  gameId,
  machineUuid,
  onClose,
  onSave,
  onEndGame,
}: GameEndTimeDialogProps) => {
  const currentEnd = toTimeOfDay(endTimeMs);
  const [selected, setSelected] = useState(currentEnd);
  const [isSaving, setIsSaving] = useState(false);

  const selectedMs = atTimeOfDay(endTimeMs, selected);
  const isExtending = selectedMs > endTimeMs;
  const change = describeEndTimeChange(endTimeMs, selectedMs);

  // Only the added time can conflict; the game's own schedule is left out
  const availability = useTimeSlotAvailability({
    fieldId: machineUuid,
    startDate: new Date(endTimeMs),
    startTime: currentEnd,
    endTime: isExtending ? selected : undefined,
    excludeScheduleId: gameId,
  });
  const status = !isExtending
    ? null
    : availability.isError
      ? "error"
      : availability.isPending
        ? "checking"
        : availability.events.length > 0
          ? "conflict"
          : "available";

  const canSave =
    selected !== currentEnd &&
    selectedMs > now &&
    (!isExtending || status === "available");

  const save = async () => {
    setIsSaving(true);
    try {
      await onSave(Math.floor(selectedMs / 1000));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ThemedDialog
      open
      onOpenChange={(open) => !open && !isSaving && onClose()}
      defaultCancelButton={false}
      size="sm"
    >
      {({ setScrollContainer }) => (
        <DialogScrollableContent setScrollContainer={setScrollContainer}>
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">
              Time Remaining:{" "}
              <span data-testid="game-end-time-remaining-value">
                {remaining}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pb-2">
            <div className="space-y-3">
              <Label className="text-sm text-gray-900 dark:text-white">
                End Time
              </Label>
              <div className="flex items-center gap-4">
                <div
                  className="w-40"
                  data-testid="game-end-time-select"
                  data-value={selected}
                >
                  <TimePicker
                    value={selected}
                    onValueChange={setSelected}
                    options={getEndTimeOptions(TIME_OPTIONS, endTimeMs, now)}
                    disabled={isSaving}
                  />
                </div>
                <Button
                  variant="secondary-flat-light"
                  isDisabled={!canSave || isSaving}
                  isLoading={isSaving}
                  aria-busy={isSaving}
                  onClick={() => void save()}
                  data-testid="game-end-time-dialog-confirm-button"
                  className="flex-1 rounded-full"
                >
                  Save
                </Button>
              </div>

              {change && (
                <p
                  data-testid="game-end-time-change-status"
                  data-value={Math.round((selectedMs - endTimeMs) / 60_000)}
                  className="text-sm text-gray-900 dark:text-white"
                >
                  {change}
                </p>
              )}

              {status && (
                <AvailabilityMessage
                  status={status}
                  conflicts={availability.events}
                  endLabel={formatTimeOfDay(selectedMs)}
                />
              )}
            </div>

            <Button
              variant="destructive-outline"
              onClick={onEndGame}
              isDisabled={isSaving}
              data-testid="game-end-time-end-game-button"
              className="w-full rounded-full"
            >
              End Game Now
            </Button>
          </div>
        </DialogScrollableContent>
      )}
    </ThemedDialog>
  );
};

type AvailabilityStatus = "checking" | "error" | "conflict" | "available";

const AvailabilityMessage = ({
  status,
  conflicts,
  endLabel,
}: {
  status: AvailabilityStatus;
  conflicts: ReturnType<typeof useTimeSlotAvailability>["events"];
  endLabel: string;
}) => (
  <div
    data-testid="game-end-time-availability-status"
    data-status={status}
    className={cn(
      "space-y-1 text-sm",
      status === "checking" && "text-tagging-muted",
      (status === "error" || status === "conflict") &&
        "text-red-600 dark:text-red-400",
      status === "available" && "text-green-600 dark:text-green-400",
    )}
  >
    {status === "checking" && <p>Checking the field's schedule…</p>}
    {status === "error" && (
      <p>Couldn't check the field's schedule. Try again.</p>
    )}
    {status === "conflict" &&
      conflicts.map((event, index) => (
        <p key={index}>
          <span className="font-bold">{getEventLabel(event)}</span> (
          {getEventTimeRange(event)}) is scheduled on this field. Choose an
          earlier end time.
        </p>
      ))}
    {status === "available" && (
      <p>No conflicts: the game can safely be extended to {endLabel}.</p>
    )}
  </div>
);
